<script>
  import { _, locale as appLocale, locales as appLocales } from '@sveltia/i18n';
  import { Alert, Divider, Option, Select, Toast } from '@sveltia/ui';

  import { appLocaleLoading } from '$lib/services/app/i18n';
  import { setState } from '$lib/services/app/onboarding';
  import { getLocaleLabel } from '$lib/services/contents/i18n';
  import { AUTO_PREF_VALUE, prefs } from '$lib/services/user/prefs.svelte';

  /**
   * @import { SettingsPanelOnChangeArgs } from '$lib/types/private';
   */

  /**
   * @typedef {object} Props
   * @property {(detail: SettingsPanelOnChangeArgs) => void} [onChange] `change` event handler.
   */

  /** @type {Props} */
  let {
    /* eslint-disable prefer-const, no-unused-vars */
    onChange = undefined,
    /* eslint-enable prefer-const, no-unused-vars */
  } = $props();

  /**
   * Locale list sorted by label.
   */
  const locales = $derived(
    appLocales
      .map((code) => {
        const localizedLabel = getLocaleLabel(code, { displayLocale: appLocale.current }) ?? code;
        const nativeLabel = getLocaleLabel(code, { displayLocale: code }) ?? code;

        const label =
          localizedLabel === nativeLabel ? localizedLabel : `${localizedLabel} — ${nativeLabel}`;

        return {
          value: code,
          searchValue: `${label} (${code})`,
          label,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)),
  );

  /**
   * Selected language preference, which is {@link AUTO_PREF_VALUE} rather than a locale code while
   * the UI follows the browser’s language settings.
   */
  const selectedLocale = $derived(prefs.locale ?? AUTO_PREF_VALUE);

  /**
   * Locale being switched to. Unlike `appLocaleLoading.current`, this keeps the last value, so the
   * message doesn’t disappear while the toast is fading out.
   */
  let switchingLocale = $state('');

  $effect(() => {
    if (appLocaleLoading.current) {
      switchingLocale = appLocaleLoading.current;
    }
  });
</script>

<section>
  <h3>{_('prefs.language.ui_language.title')}</h3>
  <div role="none">
    {#key appLocale.current}
      <Select
        aria-label={_('prefs.language.ui_language.select_language')}
        value={selectedLocale}
        onChange={(event) => {
          prefs.locale = event.detail.value;
          // Suppress the new language infobar, which is pointless once the user has explicitly
          // picked a language here
          setState('newLanguageCta', true);
        }}
      >
        <Option
          value={AUTO_PREF_VALUE}
          label={_('automatic')}
          selected={selectedLocale === AUTO_PREF_VALUE}
        />
        <Divider />
        {#each locales as { value, searchValue, label } (value)}
          <Option {value} {searchValue} {label} selected={value === selectedLocale} />
        {/each}
      </Select>
    {/key}
  </div>
</section>

<!-- Hidden automatically once the strings are loaded, hence `duration={0}` -->
<Toast show={!!appLocaleLoading.current} duration={0}>
  <Alert status="info">
    {#if switchingLocale}
      {_('switching_language', {
        values: { locale: getLocaleLabel(switchingLocale) ?? switchingLocale },
      })}
    {/if}
  </Alert>
</Toast>
